import {
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import type { ReactNode } from "react";
import { PAGE_SIZE_OPTIONS } from "../lib/pagination";

export interface Column<Row> {
  key: string;
  label: string;
  sortable?: boolean;
  align?: "left" | "right" | "center";
  /** Pins the column to the right edge so it survives horizontal scrolling. */
  pinned?: boolean;
  render?: (row: Row) => ReactNode;
}

export interface SortState {
  sortOn: string;
  sort: "ASC" | "DESC";
}

interface Props<Row> {
  columns: readonly Column<Row>[];
  rows: readonly Row[];
  rowKey: (row: Row) => string | number;
  total: number;
  page: number;
  pageSize: number;
  sort?: SortState;
  loading?: boolean;
  /** Rendered in place of rows when there are none. */
  empty?: ReactNode;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSortChange?: (sort: SortState) => void;
}

const pinnedCell = {
  position: "sticky" as const,
  right: 0,
  zIndex: 2,
  bgcolor: "background.paper",
  borderLeft: 1,
  borderColor: "divider",
};

/** Server-paged, server-sorted table. The page index is zero-based, as the APIs expect. */
export function PagedTable<Row>({
  columns,
  rows,
  rowKey,
  total,
  page,
  pageSize,
  sort,
  loading = false,
  empty,
  onPageChange,
  onPageSizeChange,
  onSortChange,
}: Props<Row>) {
  function toggleSort(key: string) {
    if (!onSortChange) return;
    const next: SortState =
      sort?.sortOn === key
        ? { sortOn: key, sort: sort.sort === "ASC" ? "DESC" : "ASC" }
        : { sortOn: key, sort: "ASC" };
    onSortChange(next);
  }

  return (
    <Paper>
      {/* Reserves its own height so the table does not jump when a fetch starts. */}
      <LinearProgress sx={{ visibility: loading ? "visible" : "hidden", height: 3 }} />
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  align={column.align ?? "left"}
                  sx={{
                    whiteSpace: "nowrap",
                    ...(column.pinned ? { ...pinnedCell, zIndex: 3 } : {}),
                  }}
                >
                  {column.sortable && onSortChange ? (
                    <TableSortLabel
                      active={sort?.sortOn === column.key}
                      direction={
                        sort?.sortOn === column.key ? (sort.sort === "ASC" ? "asc" : "desc") : "asc"
                      }
                      onClick={() => toggleSort(column.key)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={columns.length}>{empty}</TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={rowKey(row)} hover>
                  {columns.map((column) => (
                    <TableCell
                      key={column.key}
                      align={column.align ?? "left"}
                      sx={column.pinned ? pinnedCell : undefined}
                    >
                      {column.render
                        ? column.render(row)
                        : String((row as Record<string, unknown>)[column.key] ?? "")}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={page}
        rowsPerPage={pageSize}
        rowsPerPageOptions={PAGE_SIZE_OPTIONS}
        labelRowsPerPage="Rows per page"
        onPageChange={(_, next) => onPageChange(next)}
        onRowsPerPageChange={(event) => onPageSizeChange(Number(event.target.value))}
      />
    </Paper>
  );
}
